import agendaAPI from "../api/agendaAPI";
import { setupAttendeeChannel } from "./helpers/listeners";
import { compareTimes } from "../helpers/date";

const state = {
  errorStatus: null,
  isFilterOpen: false,
  agenda: {
    agenda: [],
    filters: {},
    buttons: {},
  },
  flatSessions: [],
  cachedFlatSessions: [],
  filters: {
    days: [],
    formats: [],
    rooms: [],
    topics: [],
    tracks: [],
    types: [],
    visiting_types: [],
    search: '',
    statuses: [],
    timeframe: [],
    my_presentations: [],
  },
  cachedFilters: null,
  buttons: null,

  total: 0,
  filtersUsed: 0,
  // filterResult: {
  //   days: [],
  //   formats: [],
  //   rooms: [],
  //   topics: [],
  //   tracks: [],
  //   types: [],
  //   visiting_types: [],
  //   search: '',
  //   statuses: [],
  //   timeframe: [],
  //   my_presentations: [],
  // },

  visibleSessionsCount: 10,
  defaultSessionsCount: 10,
  isFetching: false,
  hasFetchedData: false,
  isLoadedSessionDetails: false,

  isGroupedView: false,
  groupedSessions: [],
  exact_time: null,

  // Спрощена навігація - тільки один об'єкт
  navigationHistory: null,
  savedStateBeforeDetails: null, // Зберігаємо стан перед переходом до деталей,

  myScheduleView: 'list',
};

const actions = {
  async runAttendeeListeners({ state, commit, dispatch }, {attendeeId, conferenceId}) {
    try {
      const echo = await dispatch('echoStore/initEcho', null, {root: true});
      const channel = `attendee.${attendeeId}`;

      await setupAttendeeChannel(echo, channel, {commit, dispatch, state});
    } catch (error) {
      console.error('Echo initialization error:', error);
    }
  },

  async fetchAgenda({ state, commit, dispatch }, {attendeeId, conferenceId}) {
    if (state.hasFetchedData) {
      if (!state.isLoadedSessionDetails) {
        await dispatch("resetAgendaAndFilters");
      } else {
        commit("updateIsLoadedSessionDetails", false);
      }
      return;
    }

    try {
      commit("updateIsFetching", true);
      const { data: { data } } = await agendaAPI.fetch(attendeeId);

      const flatSessions = data.agenda.flatMap(day =>
        day.sessions.map(session => ({
          ...session,
          day_id: Number(day.id),
        }))
      );

      commit("updateFlatSessions", flatSessions);
      commit("updateCachedFlatSessions");
      commit("updateAgendaFilters", data.filters);
      commit("updateCachedAgendaFilters");
      commit("updateAgendaButtons", data.buttons);
      commit("updateTotal", flatSessions.length);
      commit("updateVisibleSessionsCount", state.defaultSessionsCount);
      commit("updateHasFetchedData", true);
      commit("updateErrorStatus", null);

      commit("updateGroupedSessions");

      await dispatch("runAttendeeListeners", {attendeeId, conferenceId});
    } catch (error){
      commit("updateErrorStatus", error?.response?.status);
      throw error;
    } finally {
      commit("updateIsFetching", false);
    }
  },

  resetAgendaAndFilters({ state, commit }) {
    commit("resetAgendaFilters");
    commit("updateFiltersUsed", 0);
    commit("resetFlatSessions");
    commit("updateTotal", state.cachedFlatSessions.length);
    commit("updateVisibleSessionsCount", state.defaultSessionsCount);
    commit("updateGroupedSessions");
  },

  updateAgendaAndFilters({state, commit}, filters) {
    // commit("updateAgendaFiltersResult", { ...filters });
    commit("updateAgendaFilters", {...filters});

    const filtersUsed = [
      filters?.days,
      filters?.tracks,
      filters?.topics,
      filters?.visiting_types,
      filters?.types,
      filters?.rooms,
      filters?.formats,
      filters?.statuses,
      filters?.my_presentations,
    ].reduce((acc, curr) => acc + (curr?.filter(i => i.is_checked).length || 0), 0);

    commit("updateFiltersUsed", filters?.search ? filtersUsed + 1 : filtersUsed);

    const dayIds = (filters.days?.filter(d => d.is_checked).map(d => Number(d.value)) || [])
      .filter(Number.isFinite);
    const trackIds = filters.tracks?.filter(t => t.is_checked).map(t => t.value) || [];
    const topicIds = filters.topics?.filter(t => t.is_checked).map(t => t.value) || [];
    const visitingTypeIds = filters.visiting_types?.filter(t => t.is_checked).map(t => t.value) || [];
    const typeIds = filters.types?.filter(t => t.is_checked).map(t => t.value) || [];
    const roomIds = filters.rooms?.filter(r => r.is_checked).map(r => r.value) || [];
    const formatIds = filters.formats?.filter(r => r.is_checked).map(r => r.value) || [];
    const statusesIds = filters.statuses?.filter(r => r.is_checked).map(r => r.value) || [];
    const myPresentationsIds = filters.my_presentations?.filter(r => r.is_checked).map(r => r.value) || [];
    const searchTerm = filters.search?.toLowerCase() || '';

    const filtered = state.cachedFlatSessions.filter((session) => {
      const sessionDayId = Number(session.day_id);

      if (dayIds.length && !dayIds.includes(sessionDayId)) return false;
      if (trackIds.length && !session.tracks?.some((t) => trackIds.includes(t.id))) return false;
      if (topicIds.length && !session.topics?.some((t) => topicIds.includes(t.id))) return false;
      if (visitingTypeIds.length && !visitingTypeIds.includes(session.visiting_type)) return false;
      if (typeIds.length && !typeIds.includes(session.type)) return false;
      if (roomIds.length && (!session.room || !roomIds.includes(session.room.id))) return false;
      if (formatIds.length && !formatIds.includes(session.format)) return false;
      if (myPresentationsIds.length && !session.my_presentations) return false;

      if (statusesIds.length) {
        const inProgress = session.in_progress;
        const evaluated = session.buttons?.evaluation?.completed;
        const sessionStatus = inProgress ? 0 : evaluated ? 1 : null;
        if (!statusesIds.includes(sessionStatus)) return false;
      }

      if (filters.timeframe.length) {
        const isInPeriod = compareTimes(session.start_at_formatted, filters.timeframe[0]);
        const isInPeriodLast = compareTimes(filters.timeframe[1], session.start_at_formatted);
        if (!isInPeriod || !isInPeriodLast) return false;
      }

      if (filters.exact_time) {
        if (session.start_at_formatted !== filters.exact_time.start_at_formatted ||
          session.end_at_formatted !== filters.exact_time.end_at_formatted) {
          return false;
        }
      }

      if (searchTerm) {
        const presenterList = session.presenters.map((p) => p.name).join(',').toLowerCase();
        if (
          !session.name.toLowerCase().includes(searchTerm) &&
          !session.description.toLowerCase().includes(searchTerm) &&
          !session.start_at_formatted.toLowerCase().includes(searchTerm) &&
          !session.end_at_formatted.toLowerCase().includes(searchTerm) &&
          !presenterList.includes(searchTerm)
        ) {
          return false;
        }
      }

      return true;
    });

    commit("updateFlatSessions", filtered);
    commit("updateTotal", filtered?.length);
    commit("updateVisibleSessionsCount", state.defaultSessionsCount);
    commit("updateGroupedSessions");
  },

  updateFlatSessionById({state, commit}, { id, session }) {
    commit('patchSessionById', { id, session });
    commit("updateGroupedSessions");
  },

  replaceFlatSessions({ state, commit }, sessionsToUpdate) {
    const mergeList = (list) =>
      list.map(session => {
        const replacement = sessionsToUpdate.find(s => s.id === session.id);
        return replacement ? { ...session, ...replacement } : session;
      });

    commit("updateFlatSessions", mergeList(state.flatSessions));
    commit("setCachedFlatSessions", mergeList(state.cachedFlatSessions));
    commit("updateGroupedSessions");
  },

  toggleGroupedView({ commit }, isGrouped) {
    commit("setIsGroupedView", isGrouped);
  },

  openFilterMobile({commit}){
    commit('updateIsFilterOpen', true);
  },

  closeFilterMobile({commit}){
    commit('updateIsFilterOpen', false);
  }
};

const mutations = {
  updateAgendaFilters(state, payload) {
      state.filters = payload;
  },

  updateAgendaAgenda(state, payload) {
    state.agenda.agenda = payload;
  },

  updateTotal(state, payload) {
    state.total = payload;
  },

  updateIsLoadedSessionDetails(state, payload) {
    state.isLoadedSessionDetails = payload;
  },

  updateAgendaButtons(state, payload) {
    state.buttons = payload;
  },

  updateFiltersUsed(state, payload) {
    state.filtersUsed = payload;
  },

  updateCachedFlatSessions(state) {
    state.cachedFlatSessions = JSON.parse(JSON.stringify(state.flatSessions));
  },

  updateCachedAgendaFilters(state) {
    state.cachedFilters = JSON.parse(JSON.stringify(state.filters));
  },

  resetFlatSessions(state) {
    state.flatSessions = JSON.parse(JSON.stringify(state.cachedFlatSessions));
  },

  resetAgendaFilters(state) {
    // state.filters = JSON.parse(JSON.stringify(state.cachedFilters));
    state.filters = {
      ...JSON.parse(JSON.stringify(state.cachedFilters)),
      search: ''
    };
  },

  updateFlatSessions(state, payload) {
    state.flatSessions = payload;
  },

  updateVisibleSessionsCount(state, payload) {
    state.visibleSessionsCount = payload;
  },

  updateIsFetching(state, isFetching) {
    state.isFetching = isFetching;
  },

  updateHasFetchedData(state, payload) {
    state.hasFetchedData = payload;
  },

  updateErrorStatus(state, errorStatus) {
    state.errorStatus = errorStatus;
  },

  patchSessionById(state, { id, session }) {
    const patch = (list) => {
      const idx = list.findIndex(s => s.id === id);
      if (idx !== -1) {
        list.splice(idx, 1, { ...list[idx], ...session });
      }
    };
    patch(state.flatSessions);
    patch(state.cachedFlatSessions);
  },

  setCachedFlatSessions(state, payload) {
    state.cachedFlatSessions = JSON.parse(JSON.stringify(payload));
  },

  setIsGroupedView(state, isGrouped) {
    state.isGroupedView = isGrouped;
  },

  updateGroupedSessions(state) {
    const grouped = new Map();

    state.flatSessions.forEach(session => {
      const timeSlotKey = `${session.start_at}|${session.end_at}`;

      if (!grouped.has(timeSlotKey)) {
        grouped.set(timeSlotKey, {
          timeSlot: {
            start_at: session.start_at,
            end_at: session.end_at,
            start_at_formatted: session.start_at_formatted,
            end_at_formatted: session.end_at_formatted,
            period_formatted: session.period_formatted,
            day_id: session.day_id,
            happens_at: session.happens_at,
          },
          sessions: [],
          topics: []
        });
      }

      grouped.get(timeSlotKey).sessions.push(session);
    });

    const groupedArray = Array.from(grouped.values());

    groupedArray.forEach(group => {
      const topicsSet = new Set();
      group.sessions.forEach(session => {
        if (session.topics && Array.isArray(session.topics)) {
          session.topics.forEach(topic => {
            if (topic.title) {
              topicsSet.add(topic.title);
            }
          });
        }
      });
      group.topics = Array.from(topicsSet);
    });

    state.groupedSessions = groupedArray.sort((a, b) => {
      const dateCompare = new Date(a.timeSlot.start_at) - new Date(b.timeSlot.start_at);
      if (dateCompare !== 0) return dateCompare;
      return a.timeSlot.start_at.localeCompare(b.timeSlot.start_at);
    });
  },

  updateIsFilterOpen(state, payload) {
    state.isFilterOpen = payload;
  },

  setNavigationHistory(state, payload) {
    state.navigationHistory = {
      scrollPosition: payload.scrollPosition,
      visibleSessionsCount: payload.visibleSessionsCount,
      wasGroupedView: payload.wasGroupedView
    };
  },

  clearNavigationHistory(state) {
    state.navigationHistory = null;
  },

  // Нові мутації для збереження стану перед деталями
  saveStateBeforeDetails(state, payload) {
    state.savedStateBeforeDetails = {
      navigationHistory: payload.navigationHistory,
      scrollPosition: payload.scrollPosition,
      currentView: payload.currentView
    };
  },

  clearSavedStateBeforeDetails(state) {
    state.savedStateBeforeDetails = null;
  },

  setMyScheduleView(state, view) {
    state.myScheduleView = view;
  },
};

const getters = {
  isLoadedSessionDetails(state) {
    return state.isLoadedSessionDetails;
  },

  filters(state) {
    return state.filters;
  },

  // filterResult(state) {
  //   return state.filterResult;
  // },

  cachedFilters(state) {
    return state.cachedFilters;
  },

  buttons(state) {
    return state.buttons;
  },

  flatSessions(state) {
    return state.flatSessions;
  },

  cachedFlatSessions(state) {
    return state.cachedFlatSessions;
  },

  total(state) {
    return state.total;
  },

  filtersUsed(state) {
    return state.filtersUsed;
  },

  visibleSessionsCount(state) {
    return state.visibleSessionsCount;
  },

  defaultSessionsCount(state) {
    return state.defaultSessionsCount;
  },

  isFetching(state) {
    return state.isFetching;
  },

  errorStatus(state) {
    return state.errorStatus;
  },

  hasFetchedData(state) {
    return state.hasFetchedData;
  },

  isGroupedView(state) {
    return state.isGroupedView;
  },

  groupedSessions(state) {
    return state.groupedSessions;
  },

  groupedSessionsCount(state) {
    return state.groupedSessions.length;
  },

  visibleGroupedSessions(state) {
    return state.groupedSessions.slice(0, state.visibleSessionsCount);
  },

  isFilterOpen(state){
    return state.isFilterOpen;
  },

  navigationHistory(state) {
    return state.navigationHistory;
  },

  savedStateBeforeDetails(state) {
    return state.savedStateBeforeDetails;
  },

  myScheduleView(state) {
    return state.myScheduleView;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};